import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Clock, FileText, Euro, Download, Building, AlertCircle, CheckCircle, AlertTriangle, CreditCard } from 'lucide-react';
import { formatCurrency, formatNumber } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientPortalPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchPortalData();
  }, [token]);

  const fetchPortalData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/portal/${token}`);
      if (!res.ok) {
        if (res.status === 404) {
          setError('Deze link is ongeldig of verlopen');
        } else {
          setError('Er is een fout opgetreden');
        }
        return;
      }
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError('Kon gegevens niet laden');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async (invoiceId) => {
    try {
      window.open(`${API_URL}/api/portal/${token}/invoice/${invoiceId}/pdf`, '_blank');
    } catch (err) {
      console.error('Kon PDF niet downloaden');
    }
  };

  const getStatusBadge = (invoice) => {
    const status = invoice.status;
    const isOverdue = invoice.is_overdue;
    const daysOverdue = invoice.days_overdue || 0;
    
    if (status === 'betaald') {
      return <Badge className="bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400">Betaald</Badge>;
    }
    if (status === 'verzonden' && isOverdue) {
      return (
        <div className="flex items-center gap-1">
          <Badge className="bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">
            Verlopen ({daysOverdue}d)
          </Badge>
        </div>
      );
    }
    if (status === 'verzonden') {
      return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400">Openstaand</Badge>;
    }
    if (status === 'concept') {
      return <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400">Concept</Badge>;
    }
    return <Badge>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Laden...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-card">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-bold text-foreground mb-2">Link Ongeldig</h2>
            <p className="text-muted-foreground">{error}</p>
            <p className="text-sm text-muted-foreground mt-4">
              Neem contact op met je leverancier voor een nieuwe link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="client-portal-page">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">Klant Portaal</h1>
                <p className="text-muted-foreground">{data?.business?.bedrijfsnaam || 'Urenbuddy'}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Welkom</p>
              <p className="font-medium text-foreground">{data?.client?.naam}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Overdue Alert */}
        {data?.invoices?.some(i => i.is_overdue) && (
          <Card className="bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-red-800 dark:text-red-200 mb-2">
                    U heeft verlopen facturen
                  </h3>
                  <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                    De onderstaande facturen zijn over de vervaldatum. Wij verzoeken u vriendelijk deze zo spoedig mogelijk te voldoen.
                  </p>
                  <div className="space-y-1">
                    {data.invoices.filter(i => i.is_overdue).map(inv => (
                      <div key={inv.invoice_id} className="text-sm text-red-700 dark:text-red-300 flex justify-between">
                        <span>{inv.invoice_nummer}</span>
                        <span className="font-semibold">{formatCurrency(inv.totaal)} ({inv.days_overdue} dagen te laat)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Info Card - Show if there are open invoices and IBAN is available */}
        {data?.company?.iban && data?.invoices?.some(i => i.status === 'verzonden') && (
          <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <CreditCard className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                    Betaalgegevens
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex flex-col sm:flex-row sm:justify-between">
                      <span className="text-blue-700 dark:text-blue-300">Bedrijfsnaam:</span>
                      <span className="font-medium text-blue-900 dark:text-blue-100">{data.company.naam}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between">
                      <span className="text-blue-700 dark:text-blue-300">IBAN:</span>
                      <span className="font-mono font-medium text-blue-900 dark:text-blue-100">{data.company.iban}</span>
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                      Vermeld bij betaling het factuurnummer als omschrijving.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Business Contact Info */}
        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Building className="w-5 h-5 text-cyan-500" />
              Contactgegevens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Bedrijf</p>
                <p className="font-medium text-foreground">{data?.business?.bedrijfsnaam || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">E-mail</p>
                <p className="font-medium text-foreground">{data?.business?.email || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Telefoon</p>
                <p className="font-medium text-foreground">{data?.business?.telefoon || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Adres</p>
                <p className="font-medium text-foreground">
                  {data?.business?.adres ? `${data.business.adres}, ${data.business.postcode} ${data.business.plaats}` : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-cyan-100 dark:bg-cyan-950 rounded-xl">
                  <FileText className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Facturen</p>
                  <p className="text-2xl font-bold text-foreground">
                    {data?.invoices?.length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 dark:bg-green-950 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Betaald</p>
                  <p className="text-2xl font-bold text-foreground">
                    {data?.invoices?.filter(i => i.status === 'betaald').length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 dark:bg-red-950 rounded-xl">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Verlopen</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {data?.invoices?.filter(i => i.is_overdue).length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 dark:bg-purple-950 rounded-xl">
                  <Euro className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Openstaand</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(
                      data?.invoices
                        ?.filter(i => i.status === 'verzonden')
                        ?.reduce((sum, i) => sum + (i.totaal || 0), 0) || 0
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Invoices Table */}
        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="text-foreground">Facturen</CardTitle>
            <CardDescription>Overzicht van alle facturen</CardDescription>
          </CardHeader>
          <CardContent>
            {data?.invoices?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-muted-foreground">Factuurnummer</TableHead>
                    <TableHead className="text-muted-foreground">Datum</TableHead>
                    <TableHead className="text-muted-foreground">Vervaldatum</TableHead>
                    <TableHead className="text-muted-foreground">Bedrag</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground text-right">Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.invoices.map(invoice => (
                    <TableRow key={invoice.invoice_id} className={invoice.is_overdue ? 'bg-red-50 dark:bg-red-950/30' : ''} data-testid={`invoice-row-${invoice.invoice_id}`}>
                      <TableCell className="font-medium text-foreground">
                        {invoice.invoice_nummer}
                        {invoice.reminder_count > 0 && (
                          <span className="ml-2 text-xs text-cyan-600 dark:text-cyan-400">
                            ({invoice.reminder_count}x herinnerd)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-foreground">{invoice.datum}</TableCell>
                      <TableCell className={invoice.is_overdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-foreground'}>
                        {invoice.vervaldatum}
                      </TableCell>
                      <TableCell className="text-foreground">
                        {formatCurrency(invoice.totaal)}
                      </TableCell>
                      <TableCell>{getStatusBadge(invoice)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadPdf(invoice.invoice_id)}
                          data-testid={`download-pdf-${invoice.invoice_id}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nog geen facturen</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Time Entries Summary */}
        {data?.time_entries?.length > 0 && (
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-foreground">Recente Uren</CardTitle>
              <CardDescription>Laatste 10 urenregistraties</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-muted-foreground">Datum</TableHead>
                    <TableHead className="text-muted-foreground">Beschrijving</TableHead>
                    <TableHead className="text-muted-foreground text-right">Uren</TableHead>
                    <TableHead className="text-muted-foreground text-right">Bedrag</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.time_entries.slice(0, 10).map((entry, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-foreground">{entry.datum}</TableCell>
                      <TableCell className="text-foreground">{entry.beschrijving || '-'}</TableCell>
                      <TableCell className="text-right text-foreground">{formatNumber(entry.uren)}</TableCell>
                      <TableCell className="text-right text-foreground">{formatCurrency(entry.totaal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-border mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-sm text-muted-foreground">
          <p>Powered by Urenbuddy - Tijd- en kilometerregistratie voor freelancers</p>
        </div>
      </div>
    </div>
  );
}
